/* eslint-disable react/sort-comp */
import moment from 'moment';
import PropTypes from 'prop-types';
import ChangeLog from '@upstart/harmony/lib/pencil-changelog';
import { isMarketScanIncentivesEnabled } from '@upstart/harmony/lib/dealer-utils';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { Dimmer, Loader } from 'semantic-ui-react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import {
  find,
  isNumber,
  get,
  getOr,
  set,
  first,
  size,
  cloneDeep,
  isEmpty,
  isEqual,
  isNil,
} from 'lodash/fp';
import { orderBy } from 'lodash';
import {
  PencilStatus,
  PencilType,
  CrmType,
  FinanceType,
  CLIENT_TYPE,
  Origin,
  DEFAULT_APR,
  RequestStatus,
  FAUX_TRADE_IN_ID,
  FinanceResponseStatus,
} from '@upstart/harmony/lib/constants';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import {
  getPaymentOffer,
  getPaymentOffers,
} from '@upstart/harmony/lib/payment-utils';
import { MdClear } from 'react-icons/md';
import { BiBell } from 'react-icons/bi';
import {
  sendData,
  getCarInfo,
  getSendDealEventProperties,
  getUpstartOfferEventProperties,
} from 'util/amplitude';
import {
  amplitudeEvents,
  amplitudeEventsPricingAddVehicle,
  Action,
  amplitudeEventsShareDeal,
  amplitudeEventsDeskingCTR,
} from 'util/amplitudeEvents';
import { CustomerFormPanel } from 'components/CustomerFormPanel';
import { EmbeddedNotes } from 'components/PencilEditor/Notes';
import {
  subscribeToWebsockets,
  unsubscribeFromWebsockets,
} from './WebsocketSubscription';
import {
  createPencilFromDesking,
  getApplicableDeskingOffers,
  updateDeskingTradeIns,
} from '../../util/deskingUtils';
import {
  getFinanceApplicationForResponse,
  hasLeaseOffers,
  getPencilWithTradeIns,
} from '../../util/pencilUtils';
import { getSelectedFinanceType } from '../../util/paymentUtils';
import {
  isEmbeddedInCommandCenter,
  isEmbeddedInFniHome,
  isEmbeddedStandaloneHistoryAndNotes,
} from '../../util/commandCenter';
import { PageMessage } from '../../components/GlobalUI';
import StickerModal from '../../components/StickerModal';
import ErrorMessage from '../../components/ErrorMessage';
import ConfirmModal from '../../components/ConfirmModal';
import SidebarContainer from '../SidebarContainer';
import dealsActions from '../../redux/deals/actions';
import dealerActions from '../../redux/dealers/actions';
import sourceMappingActions from '../../redux/sourceMappings/actions';
import pencilsActions from '../../redux/pencils/actions';
import * as deskingActions from '../../redux/desking/actions';
import formsActions from '../../redux/forms/actions';
import * as translationsActions from '../../redux/translations/actions';
import pencilHistoryActions from '../../redux/pencilHistory/actions';
import DealService from '../../services/DealService';
import PencilService from '../../services/PencilService';

import {
  HeaderNotice,
  HeaderNavigation,
  SendButton,
  DeskingOffers,
  SentModal,
  NotesModal,
} from '../../components/PencilEditor';
import PencilTopSection from './PencilTopSection/PencilTopSection';
import VehicleSelection from './VehicleSelection';
import './PencilEditor.scss';
import notificationSound from '../../assets/sound/notification.mp3';
import { updateDocumentTitle, updateDocumentFavIcon } from '../../util/utils';
import DMSCustomerModal from '../../components/PencilEditor/DMSCustomerModal';
import {
  isDMSFlowEnabled as isDMSFlowEnabledUtil,
  isSentToDMS,
} from '../../util/dmsUtils';
import PencilHistory from '../../components/PencilHistory';
import { validateDeskingOffer } from '../../components/DeskingEditor/Validator';
import InlineMessage from '../../components/GlobalUI/InlineMessage/InlineMessage';
import TradeInsModal from '../../components/DeskingEditor/TradeInsModal';
import CrmNoteModal from './CrmNoteModal';
import { CRM_PUSH_ALERT_MSG_FOR_NON_ACCEPTED_OFFER } from './constants';
import FormsAndAttachedFiles from '../FormsAndAttachedFiles';
import FinanceResponseService from '../../services/FinanceResponseService';
import { isApprovedCounter } from '../../util/financeResponseUtils';
import { Tabs } from './types';

const updateWebsocketSubscriptionsImpl = (
  getState,
  setState,
  updatePencil,
  props,
) => {
  const dealId = get('deal.id', props);
  const carId = get('car.id', props);
  const state = getState();
  if (state.subscribedDealId === dealId && state.subscribedCarId === carId) {
    return;
  }
  const dealFinancialsUpdateCallback = payload => {
    const { update } = payload;
    if (update.downPayment) {
      const newLocalPencil = set(
        'data.downPayment',
        update.downPayment,
      )(getState().localPencil);
      updatePencil(newLocalPencil);
    }
  };
  unsubscribeFromWebsockets(state.subscribedDealId, state.subscribedCarId);
  subscribeToWebsockets({
    dealId,
    carId,
    dealRequest: props.actions.dealRequest,
    deskingRequest: props.actions.deskingRequest,
    dealFinancialsUpdateCallback,
  });
  setState({
    subscribedDealId: dealId,
    subscribedCarId: carId,
  });
  if (dealId && carId) {
    props.actions.deskingRequest({ dealId, carId }); // force desking request on updated ws subscriptions so new desking objects can be created
  }
};

const getFormattedAPR = apr => parseFloat((apr * 100).toFixed(10));

const getOfferTermMessage = (financeType, { apr, months, mileage }) => {
  switch (financeType) {
    case FinanceType.PURCHASE:
      return `${financeType}, ${months} months @${getFormattedAPR(apr)}%`;
    case FinanceType.LEASE:
      return `${financeType}, ${months} months @${mileage} miles`;
    case FinanceType.CASH:
    default:
      return financeType;
  }
};

const sendAddVehicleAmplitudeEvent = (event, properties) => {
  const eventName = amplitudeEventsPricingAddVehicle[event];
  if (eventName) {
    sendData(eventName, properties);
  }
};

class CarPencilEditor extends Component {
  constructor() {
    super();
    this.state = {
      financeType: null,
      showApplicantForm: false,
      showVehicleSelection: false,
      showStickerModal: false,
      showNotesModal: false,
      showDMSCustomerModal: false,
      showDMSCustomerModalForOffer: false,
      showTradeinsModal: false,
      validationErrors: null,
      financeApplicationError: null,
      note: '',
      localPencil: { data: {} },
      deskingOfferForDMSPush: null,
      changeLog: null,
      reset: null,
      revert: null,
      activeOffers: {},
      viewForms: false,
      subscribedDealId: null,
      subscribedCarId: null,
      showExpiredIncentiveNotice: false,
      showUnavailableAccessoriesNotice: false,
      showLendingOffers: false,
      financeOfferCount: 0,
      financeApplicationCount: 0,
      showPushToCRMTooltip: false,
      showRicTab: false,
      showCustomerSelectedOffer: false,
      dealTradeIns: null,
      notesReadAt: null,
      showOnlineHistoryModal: false,
      submittingForm: false,
      isViewingCurrentPencilVersion: true,
      deskingFinanceResponses: null,
    };
  }

  pencilEditorContainer = React.createRef();

  readMsgCount = 0;
  totalMsgCount = 0;
  notifiedFlag = false;
  notify = new Audio(notificationSound);

  componentDidMount() {
    const { dealId, dealer } = this.props.match.params;
    this.updateWebsocketSubscriptions();
    window.addEventListener('focus', this.onWindowFocus);
    if (this.props.pencils) {
      // Set readMsgCount as soon as we can
      this.readMsgCount = this.getMsgCount(this.props.pencils);
    }

    this.props.actions.pencilListRequest({ dealId });
    if (this.props.pencil) {
      this.setLocalPencil(this.props.pencil);
    }
    if (dealId) {
      this.props.actions.readFormsRequest(dealId);
    }
    if (dealer?.id) {
      this.props.actions.formsRequest(dealer?.id);
      this.props.actions.translationRequest({ dealerId: dealer?.id });
      this.props.actions.dealerPriceRulesFetchRequest(dealer.id);
    }
    const propDealer = this.props.dealer;
    if (propDealer && propDealer.id) {
      this.props.actions.sourceMappingRequest(propDealer.id);
    }

    this.sendAmplitudeEvent(amplitudeEvents.DESKING_PENCIL_START);
  }

  componentDidUpdate(prevProps) {
    const { dealId } = this.props.match.params;
    const { car, pencils, deal, dealer, pencil } = this.props;
    this.updateWebsocketSubscriptions();

    const { id: dealerId } = dealer || {};
    let unreadMsgCount = 0;

    if (pencils && !prevProps.pencils) {
      // Set readMsgCount as soon as we can
      this.readMsgCount = this.getMsgCount(this.props.pencils);
    }
    // props.pencils.salesPersonNote;
    this.totalMsgCount = this.getMsgCount(this.props.pencils);
    if (this.totalMsgCount > this.readMsgCount) {
      unreadMsgCount = this.totalMsgCount - this.readMsgCount;
      if (!this.notifiedFlag) {
        // Never assume that a sound was played
        // Create a promise to play the sound
        // and test results below
        // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
        const audioPromise = this.notify.play();
        if (audioPromise !== undefined) {
          audioPromise
            .then(() => {
              // Autoplay was allowed by browser.
              // Let's go ahead and play the sound for real
              this.notify.play();
              this.notifiedFlag = true;
            })
            .catch(e => {
              // Autoplay was prevented by browser.
              // Nothing to see here.  Move along.
              // eslint-disable-next-line no-console
              console.error('Could not play notification sound: ', e);
            });
        }
      }
    }

    // Pencil Page has a unique Page Title
    document.title = updateDocumentTitle(
      this.props.location?.pathname,
      unreadMsgCount,
      this.getSalesPersonName(),
    );
    // Reset Fav Icon
    updateDocumentFavIcon(unreadMsgCount);

    if (
      car &&
      !this.props.deskingFetching &&
      !this.props.desking &&
      !this.props.deskingError
    ) {
      this.props.actions.deskingRequest({ dealId, carId: car.id });
    }
    if (prevProps?.match?.params?.dealId !== dealId) {
      this.props.actions.readFormsRequest(dealId);
    }
    if (prevProps?.dealer?.id !== dealerId) {
      this.props.actions.formsRequest(dealerId);
      this.props.actions.translationRequest({ dealerId });
      this.props.actions.dealerPriceRulesFetchRequest(dealerId);
    }

    if (
      (isEmpty(prevProps.dealer) && this.props.dealer) ||
      !this.state.financeType
    ) {
      const financeType =
        dealer && car
          ? getSelectedFinanceType({ car, deal, dealer })
          : FinanceType.CASH;
      this.setState({ financeType });
    }

    // if it's a reset or a revert, update localPencil with new pencil
    if (
      (isEmpty(prevProps.pencil) && !isEmpty(this.props.pencil)) ||
      (get('pencil.id')(prevProps) !== get('pencil.id')(this.props) &&
        !isEmpty(this.props.pencil))
    ) {
      if (
        this.state.reset ||
        this.state.revert ||
        this.props.pencil.status === PencilStatus.EXPIRED
      ) {
        this.setLocalPencil({
          ...this.props.pencil,
          status: null,
          id: null,
        });
        this.resetChangelog(this.props.pencil);
      } else {
        this.setLocalPencil({ ...this.props.pencil });
      }

      if (isEmpty(prevProps.pencil)) {
        // initialize changelog using the first pencil
        this.resetChangelog(this.props.pencil);
      }

      this.setState({ showPushToCRMTooltip: !!pencil.pushedToCrmAt });

      if (
        this.state.showNotesModal &&
        (!this.state.notesReadAt ||
          this.state.notesReadAt.isBefore(this.props.pencil.updatedAt))
      ) {
        // If the notes modal is open, then the FM is reading any incoming notes in real-time
        this.setState({ notesReadAt: moment() });
      }
    }

    // init first pencil
    if (!isEqual(prevProps.pencils, pencils)) {
      if (size(pencils) > 0) {
        // a new pencil was received. get the latest pencil.
        const currentPencil = first(pencils);
        this.props.actions.pencilRequest({
          dealId,
          pencilId: currentPencil.id,
        });
      } else if (isEmpty(this.props.pencil)) {
        // no pencils yet. get the default.
        this.props.actions.initPencil({ dealId, carId: car.id });
      }
    }

    // update local pencil's tradein data if it has been updated in the pencil
    if (
      !this.state.reset &&
      !this.state.revert &&
      prevProps.pencil?.data &&
      pencil?.data &&
      !isEqual(
        prevProps.pencil?.data?.pencilTradeIns,
        pencil.data?.pencilTradeIns,
      ) &&
      !isEqual(
        this.state.localPencil?.data?.pencilTradeIns,
        pencil.data?.pencilTradeIns,
      )
    ) {
      const newLocalPencil = set(
        'data.pencilTradeIns',
        pencil.data?.pencilTradeIns,
      )({ ...this.state.localPencil });
      newLocalPencil.data.tradeACV = pencil.data?.tradeACV;
      newLocalPencil.data.tradeInAllowance = pencil.data?.tradeInAllowance;
      newLocalPencil.data.tradeInTaxCredit = pencil.data?.tradeInTaxCredit;
      newLocalPencil.data.tradePayoff = pencil.data?.tradePayoff;
      this.setLocalPencil(newLocalPencil);
    }

    if (pencil?.data && deal.tradeIns !== prevProps.deal.tradeIns) {
      this.setLocalPencil(getPencilWithTradeIns(pencil, deal));
    }

    if (
      this.props.pencilModalText &&
      this.props.pencilModalText !== prevProps.pencilModalText &&
      typeof this.modal?.open === 'function'
    ) {
      this.modal.open();
    }
  }

  componentWillUnmount() {
    unsubscribeFromWebsockets(
      this.state.subscribedDealId,
      this.state.subscribedCarId,
    );
    window.removeEventListener('focus', this.onWindowFocus);
  }

  sendAmplitudeEvent = event => {
    const { car } = this.props;
    sendData(event, getCarInfo(car));
  };

  updateWebsocketSubscriptions = () =>
    updateWebsocketSubscriptionsImpl(
      () => this.state,
      this.setState.bind(this),
      this.updatePencil.bind(this),
      this.props,
    );

  // Reset message count and fav icon on window focus
  onWindowFocus = () => {
    const { pencils, location } = this.props;
    if (pencils) {
      this.readMsgCount = this.getMsgCount(pencils);
      document.title = updateDocumentTitle(
        location.pathname,
        null,
        this.getSalesPersonName(),
      );
      updateDocumentFavIcon(0);
      this.notifiedFlag = false;
    }
  };

  getMsgCount = pencils =>
    orderBy(pencils, 'updatedAt').map(p => p.salesPersonNote).length +
    orderBy(pencils, 'updatedAt').filter(
      p => !isEmpty(p.selectedFinanceResponse),
    ).length;

  onNoteChange = (note, callback) => this.setState({ note }, callback);

  setLocalPencil = newLocalPencil => {
    const localPencil = cloneDeep(newLocalPencil);
    this.setState({ localPencil });
  };

  setIsViewingCurrentPencilVersion = flag =>
    this.setState({ isViewingCurrentPencilVersion: flag });

  setDeskingFinanceResponses = financeResponses =>
    this.setState({ deskingFinanceResponses: financeResponses });

  resetChangelog = pencil => {
    const oldPencil = cloneDeep(pencil);
    // on revert go back to old changelog
    const data = this.state.revert ? oldPencil.changeLog : {};
    this.setState({
      changeLog: new ChangeLog(oldPencil, data),
    });
  };

  // get reset notification message
  getNotification = () => {
    const { currentUser } = this.props;
    if (this.state.reset || this.state.revert) {
      // reset the change log
      const name = currentUser.firstName
        ? `${currentUser.firstName} ${currentUser.lastName}`
        : '';
      return this.state.reset
        ? `${name} reset all changes`
        : `${name} reverted to an older pencil`;
    }
    return null;
  };

  validate = pencil => {
    // html5 form validation only applies to visible fields, so we have to validate again here
    // force all residuals to be defined
    const leaseOffers = get('data.lease')(pencil);
    const validationErrors = [];
    if (
      hasLeaseOffers(pencil) &&
      find(o => !isNumber(o.residual))(leaseOffers)
    ) {
      validationErrors.push(
        'Missing Residual Value! Please enter a Residual Value to proceed.',
      );
    }

    // days to first payment should be between 0 and 365
    const daysToFirstPayment =
      get('data.daysToFirstPayment')(this.props.pencil) || 0;
    if (daysToFirstPayment < 0 || daysToFirstPayment > 365) {
      validationErrors.push(
        'Days to First Payment should be between 0 and 365',
      );
    }
    return validationErrors;
  };

  revert = (dealId, pencilId) => {
    this.setState({ revert: true });
    this.props.actions.pencilRequest({
      dealId,
      pencilId,
    });
  };

  getNewPencil = () => {
    const { car, currentUser } = this.props;

    if (this.state.reset) {
      this.state.changeLog.setResetPencilHasBeenTriggered();
    }

    // Assign SENT status
    const now = moment();
    const newPencil = {
      ...this.state.localPencil,
      carId: car.id,
      type: PencilType.MANAGER,
      status: PencilStatus.SENT,
      updatedAt: now,
      sentAt: now,
      managerNote: {
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        note: this.state.note,
        timestamp: now,
      },
      changeLog: this.state.changeLog && this.state.changeLog.getLogs(),
      notification: this.getNotification(),
    };

    return newPencil;
  };

  validatePencilForm = () => {
    const { pencil } = this.props;
    const validationErrors = this.validate(pencil);

    if (validationErrors.length) {
      this.pencilEditorContainer.current.scrollTo(0, 0);
      this.setState({ validationErrors });
      throw new Error(validationErrors.join(', '));
    }
  };

  resetPencilForm = () =>
    this.setState({
      validationErrors: [],
      note: '',
      reset: false,
      revert: false,
    });

  /**
   * Checks that there exists at least one selected offer.
   * @returns boolean
   */
  hasAtLeastOneSelectedOffer = () => {
    const offers = this.props.desking?.deskingOffers ?? [];
    return offers.some(deskingOffer => deskingOffer.isSelectedOffer);
  };

  getSelectedFinanceOffers = () => {
    const financeApplications =
      get('financeApplications')(this.state.localPencil) ||
      get('data.financeApplications')(this.state.localPencil);
    return financeApplications?.flatMap(app =>
      app?.responses?.filter(res => res?.selected),
    );
  };

  updateApprovedCounterOffers = async newPencil => {
    const financeResponsesToUpdate = [];
    // transition responses that are in status COUNTERED but do not need adjustment to approved if sent to salesperson
    const financeApplications =
      newPencil.financeApplications || newPencil.data?.financeApplications;
    const pencilFinanceApplications = financeApplications?.map(fa => {
      const updatedResponses = fa.responses.map(fr => {
        if (isApprovedCounter(fr) && fr.selected) {
          const newResponse = {
            ...fr,
            status: FinanceResponseStatus.APPROVED,
          };
          financeResponsesToUpdate.push(newResponse);
          return newResponse;
        }

        return fr;
      });

      fa.responses = updatedResponses;
      return fa;
    });

    // need to persist the updated status on the actual finance response object itself as well as the pencil
    if (financeResponsesToUpdate.length) {
      await Promise.all(
        financeResponsesToUpdate.map(fr => FinanceResponseService.update(fr)),
      );
    }

    return pencilFinanceApplications;
  };

  onSubmit = async () => {
    try {
      this.setState({ submittingForm: true });
      this.validatePencilForm();

      const newPencil = this.getNewPencil();
      this.resetPencilForm();

      const dealId = this.props.deal.id;
      const hasUpdatedTradeIns = this.state.dealTradeIns;

      // Clear deal alerts
      if (this.props.deal.ownershipOptionsStatus === RequestStatus.REQUESTED) {
        await DealService.updateDeal({
          dealId,
          body: {
            ownershipOptionsStatus: RequestStatus.ACCEPTED,
          },
        });
      }

      /* Bit of a hack for REL-17502 - amplitude event logging for selected offers
        DESKING_FINANCE_OFFER_SENT event should be sent for each selected finance offer.
        The problem is that pencil.financeApplications.responses is used to determine selected finance offers which
        cannot be expected to be up to date since updates to upstart offers happen directly to the Finance Response record).

        To fix this I pulled the financeResponses state from DeskingOffers to this parent component and use it to cross reference up to date Response data.
       */
      if (this.isFinanceOffersTab) {
        this.getSelectedFinanceOffers().forEach(selectedPencilResponse => {
          const financeResponse = this.state.deskingFinanceResponses?.find(
            fr => fr.id === selectedPencilResponse.id,
          );
          if (financeResponse) {
            sendData(
              amplitudeEventsDeskingCTR.DESKING_FINANCE_OFFER_SENT,
              getUpstartOfferEventProperties({
                dealId,
                application: financeResponse.FinanceApplication,
                response: financeResponse,
              }),
            );
          }
        });

        const updatedFinanceApplications = await this.updateApprovedCounterOffers(
          newPencil,
        );
        // set financeApplications back to pencil with responses transition to approved
        if (updatedFinanceApplications) {
          newPencil.financeApplications = updatedFinanceApplications;
        }
      }

      if (hasUpdatedTradeIns) {
        await DealService.updateDeal({
          dealId,
          body: {
            tradeIns: this.state.dealTradeIns,
          },
        });

        this.setState({
          dealTradeIns: null,
        });
      }

      if (
        // desking object is not used to pass lending offers - directly send pencil instead
        !this.isFinanceOffersTab &&
        !newPencil.managerNote.note
      ) {
        // only use this action when there is no manager note
        if (this.hasAtLeastOneSelectedOffer(this.props.desking.deskingOffers)) {
          this.props.actions.deskingSendToSalesperson({
            deskingId: this.props.desking.id,
          });
        } else {
          PageMessage({
            type: MessageType.ERROR,
            message: 'Please select at least one offer to send.',
          });
        }
      } else if (this.state.localPencil.status !== PencilStatus.REQUESTED) {
        // check if we are responding to a pencil request
        // this is a new pencil, remove the obsolete id and salesPersonNote
        delete newPencil.id;
        delete newPencil.salesPersonNote;
        this.props.actions.pencilCreateRequest({
          pencil: newPencil,
          customSuccessMessage: newPencil.managerNote.note
            ? 'Note has been sent to Salesperson'
            : null,
        });
      } else {
        this.props.actions.pencilUpdateRequest({
          pencil: newPencil,
          customSuccessMessage: newPencil.managerNote.note
            ? 'Note has been sent to Salesperson'
            : null,
        });
      }
      // update local pencil
      this.updatePencil(newPencil);
      // start tracking changes from this new pencil onwards
      this.resetChangelog(newPencil);
    } finally {
      this.setState({ submittingForm: false });
    }
  };

  onSendSticker = (stickerData, sendParams, configPencilData) => {
    const { dealer, desking, car } = this.props;
    this.validatePencilForm();

    const currentPencil = createPencilFromDesking(
      this.state.localPencil,
      desking,
      dealer,
    );

    const newPencil = { ...currentPencil, stickerData };
    this.resetPencilForm();

    // this is a new pencil, remove the obsolete id and salesPersonNote
    delete newPencil.id;
    delete newPencil.salesPersonNote;
    this.props.actions.sendStickerRequest({
      pencil: newPencil,
      sendParams,
      configPencilData: { ...configPencilData, stickerData },
    });

    sendData(amplitudeEventsShareDeal.SHARE_DEAL_SENT, {
      ...getSendDealEventProperties(sendParams),
      ...getCarInfo(car),
    });

    this.resetChangelog(newPencil);
  };

  onPrintSticker = async actionData => {
    await this.onSubmit();
    this.props.actions.printStickerRequest(actionData);
    this.sendAmplitudeEvent(
      amplitudeEventsShareDeal.SHARE_DEAL_PREVIEW_STICKER_PRINT_CLICKED,
    );
  };

  isCRMPushEnabled = () => {
    const { dealer } = this.props;
    if (!dealer || !this.hasEnteredCustomer()) {
      return false;
    }
    const useDeskingCRMPush = dealer?.desking?.useDeskingCRMPush;
    return (
      [CrmType.ELEAD, CrmType.VINSOLUTIONS, CrmType.DGDG_SALESFORCE].includes(
        dealer.crmType,
      ) && useDeskingCRMPush
    );
  };

  pushToCRM = async () => {
    const { deal, pencil, actions, dealer, payments } = this.props;

    if (!deal.lastOpenedInStore && pencil.status !== PencilStatus.ACCEPTED) {
      const { activeOffers, localPencil, financeType } = this.state;
      const selectedOfferIndex = activeOffers[financeType] || 0;
      const selectedOffer = getPaymentOffers(payments, financeType)[
        selectedOfferIndex
      ];
      const {
        apr,
        months: term,
        mileage: selectedMileage,
        downPayment,
      } = selectedOffer;
      const { creditRating } = localPencil.data;
      const defaultApr = getOr(DEFAULT_APR, 'defaultApr')(dealer);

      const confirmed = await ConfirmModal({
        text: `Do you want to update deal with the selected offer (${getOfferTermMessage(
          financeType,
          selectedOffer,
        )}) and send information to CRM?`,
      });
      if (!confirmed) return;

      actions.pushingToCRM(true);
      await DealService.updateDeal({
        dealId: deal.id,
        body: {
          id: deal.id,
          financeType,
          downPayment,
          creditRating,
          selectedOffer,
          term,
          selectedMileage,
          apr: isNil(apr) ? defaultApr : apr,
        },
      });

      await PencilService.updatePencil(pencil.id, {
        id: pencil.id,
        status: PencilStatus.ACCEPTED,
        selectedOffer,
        selectedFinanceType: financeType,
      });
    }

    actions.pencilCRMPushRequest({ deal, pencil });
  };

  showDMSModal = () => {
    this.setState({ showDMSCustomerModal: true });
  };

  hasEnteredCustomer = () => {
    const { deal } = this.props;
    const applicant = (deal || {}).applicant || {};
    return !!applicant.firstName && !!applicant.lastName;
  };

  isMarkNoOffers = () => {
    const financeApplications =
      get('financeApplications')(this.state.localPencil) ||
      get('data.financeApplications')(this.state.localPencil) ||
      [];
    if (financeApplications.length > 0) {
      return financeApplications.some(a => a.markNoOffers);
    }

    const sendWithNoOffers =
      get('data.sendWithNoOffers')(this.state.localPencil) || false;
    return sendWithNoOffers;
  };

  isAnyDeskingOfferPaymentInProgress = () =>
    Object.values(this.props.offerPymtCalcInProgMap || {}).some(
      requestCountInProgress => requestCountInProgress > 0,
    );

  isSendDisabled = () => {
    // prevent send to salesperson when no customer info present
    if (!this.hasEnteredCustomer()) {
      return true;
    }

    if (!this.props.car || !this.props.pencil) {
      return true;
    }

    if (!this.isFinanceOffersTab) {
      return (
        this.isAnyDeskingOfferPaymentInProgress() ||
        this.hasSelectedDeskingOfferErrors()
      );
    }

    return (
      (this.isFinanceOffersTab || this.state.showLendingOffers) &&
      !this.getSelectedFinanceOffers()?.length &&
      !this.isMarkNoOffers()
    );
  };

  getSendButtonTooltip = () => {
    let tooltip;
    if (this.hasSelectedDeskingOfferErrors()) {
      tooltip = 'Errors must be resolved before you may send to Salesperson';
    } else if (this.isAnyDeskingOfferPaymentInProgress()) {
      tooltip =
        'Send to Salesperson is disabled as payments calculation is in progress';
    } else if (!this.hasEnteredCustomer()) {
      tooltip =
        'Send to Salesperson is disabled as customer details are incomplete';
    } else if (!this.props.car || !this.props.pencil) {
      tooltip =
        'Send to Salesperson is disabled as the deal has no vehicle selected';
    }

    return tooltip;
  };

  updatePencil = pencil => {
    const { dealId } = this.props.match.params;
    const { deal } = this.props;
    const clientType =
      deal.origin === Origin.ONLINE ? CLIENT_TYPE.ONLINE : CLIENT_TYPE.IN_STORE;

    // Check for new validation errors, e.g missing residuals
    if (pencil) {
      const validationErrors = this.validate(pencil);
      this.setState({ validationErrors });
    }

    this.setLocalPencil(pencil);
    this.props.actions.setPencil(pencil);
    // the local pencil is newer than the saved pencil on the server
    // so the whole pencil needs to be sent in the request
    this.props.actions.paymentsRequest({ dealId, pencil, clientType });
  };

  updateDeskingOffers = (
    deskingOffers,
    // do not recompute payments by default when updating a list of offers
    params = {
      computePayments: false,
      saveToGlobalState: true,
      isMSIncentivesEnabled: false,
    },
  ) =>
    // wrap redux action with promise
    new Promise((resolve, reject) => {
      this.props.actions.bulkUpdateDeskingOffer({
        deskingOffers,
        params,
        resolve,
        reject,
      });
    });

  updateDeskingOffer = deskingOffer => {
    this.updateDeskingOffers([deskingOffer], {
      // always recompute payments when updating a single desking offer
      computePayments: true,
      saveToGlobalState: true,
      isMSIncentivesEnabled: isMarketScanIncentivesEnabled(this.props.dealer),
    });
  };

  createDefaultDeskingOffer = ({ dealId, carId, deskingId, financeType }) => {
    this.props.actions.createDefaultDeskingOffer({
      dealId,
      carId,
      deskingId,
      financeType,
    });
  };

  duplicateDeskingOffer = deskingOffer => {
    this.props.actions.createDeskingOffer({ deskingOffer });
  };

  removeDeskingOffer = deskingOffer => {
    this.props.actions.deleteDeskingOffer({ deskingOffer });
  };

  pushDeskingOfferToCRM = async (
    deskingOffer,
    shouldDisplayAlertMessage = false,
  ) => {
    if (shouldDisplayAlertMessage) {
      const confirmed = await ConfirmModal({
        text: CRM_PUSH_ALERT_MSG_FOR_NON_ACCEPTED_OFFER,
      });
      if (!confirmed) return;
    }

    this.props.actions.pushCrmDeskingOffer({ deskingOffer });
  };

  pushDeskingOfferToDMS = async deskingOffer => {
    this.setState({
      showDMSCustomerModalForOffer: true,
      deskingOfferForDMSPush: deskingOffer,
    });
  };

  openPencilHistory = async deskingOfferId => {
    this.props.actions.offerHistoryRequest({ deskingOfferId });
    this.props.actions.openPencilHistory();
  };

  renderVersionSnapshots = async ({ deskingOfferId, version }) => {
    this.props.actions.versionSnapshotsRequest({
      deskingId: this.props.desking.id,
      deskingOfferId,
      version,
    });
  };

  getDeskingOffers = () => {
    let desking = this.props.desking;
    if (this.props.pencilHistoryOpened && this.props.versionSnapshots) {
      desking = {
        ...desking,
        deskingOffers: this.props.versionSnapshots.deskingOffers,
      };
    }
    return getApplicableDeskingOffers(
      this.props.car,
      this.props.deal,
      this.props.dealer,
      desking,
    );
  };

  getDeskingPayments = () => {
    if (this.props.pencilHistoryOpened && this.props.versionSnapshots) {
      return this.props.versionSnapshots.payments;
    }
    return this.props.deskingPayments;
  };

  getSelectedIdForPencilHistory = () => {
    if (this.props.pencilHistoryOpened && this.props.offerHistory) {
      // by default, select the first offer
      return this.props.offerHistory[0]?.deskingOfferId;
    }
    return undefined;
  };

  getDeskingOfferErrors = () => {
    const deskingOfferErrors = this.getDeskingOffers()
      .map(offer => validateDeskingOffer(offer))
      .filter(deskingError => !!deskingError.errors);
    return deskingOfferErrors;
  };

  hasSelectedDeskingOfferErrors = () => {
    const deskingOffersWithError = this.getDeskingOfferErrors();
    return deskingOffersWithError.filter(o => o.isSelected).length > 0;
  };

  updateDeskingOffersWithTradeIns = async tradeIns => {
    const updatedDeskingOffers = updateDeskingTradeIns(
      tradeIns,
      this.props.desking.deskingOffers,
    );

    this.props.desking.deskingOffers = updatedDeskingOffers;
    this.updateDeskingOffers(updatedDeskingOffers, {
      computePayments: true,
      saveToGlobalState: true,
    });
    // cause component to refresh
    this.props.actions.dealRequest({ dealId: this.props.desking.dealId });
  };

  onResetToDefault = () => {
    const {
      match: {
        params: { dealId },
      },
    } = this.props;
    this.props.actions.initPencil({ dealId, carId: this.props.car.id });
    this.setState({ reset: true });
  };

  getSalesPersonName() {
    const { user } = this.props;
    return user && `${user?.firstName} ${user?.lastName}`;
  }

  toggleOnePayLease = async () => {
    const { actions, deal, car } = this.props;
    const newOnePayLease = !deal?.onePayLease;
    await actions.dealUpdateRequest({
      ...deal,
      onePayLease: newOnePayLease,
    });

    if (car.id !== deal.car_id) {
      this.props.actions.deskingRequest({ dealId: deal.id, carId: car.id });
    }

    let pencil = cloneDeep(this.props.pencil);

    pencil = set('data.onePayLease', newOnePayLease, pencil);

    this.updatePencil(pencil);

    this.state.changeLog.updateProp('onePayLease', newOnePayLease);
  };

  renderPushToCRMTooltip() {
    return (
      <div
        className="push-to-crm-tooltip"
        onClick={() => this.setState({ showPushToCRMTooltip: false })}
      >
        <div className="title">No new changes to push to CRM</div>
        <div className="details">
          Last push at{' '}
          {moment(this.props.pencil.pushedToCrmAt).format('MM-DD-YYYY h:mm a')}
        </div>
      </div>
    );
  }

  areNewNotesAvailable = () => {
    // Show notifications icon when there is a local pencil with new notes to read
    const { localPencil, notesReadAt } = this.state;
    return (
      localPencil &&
      (!notesReadAt || notesReadAt.isBefore(localPencil.updatedAt)) &&
      ((localPencil?.salesPersonNote && !localPencil.managerNote) ||
        localPencil?.notification)
    );
  };

  get isFinanceOffersTab() {
    return this.props.match.params.currentTab === Tabs.FINANCE_OFFERS;
  }

  /**
   * Updates the url path based on the selected tab.
   * Ensures that any existing query parameters are passed along
   * @param setToFinanceOffers - boolean to determine if the path should be set to the finance offers tab
   */
  setTabPath = setToFinanceOffers => {
    const searchParams = this.props.location.search
      ? this.props.location.search
      : '';
    const { dealId } = this.props.match.params;
    this.props.history.push(
      setToFinanceOffers
        ? `/pencil/${dealId}/finance-offers${searchParams}`
        : `/pencil/${dealId}${searchParams}`,
    );
  };

  renderContent = () => {
    const { dealId } = this.props.match.params;
    const {
      car,
      deal,
      dealer,
      pencil,
      pencils,
      payments,
      user,
      currentUser,
      isLoading,
      selectedCars,
      actions,
      translations,
    } = this.props;
    const { validationErrors, financeApplicationError } = this.state;
    const accepted =
      pencil &&
      (pencil.status === PencilStatus.ACCEPTED ||
        pencil.selectedFinanceResponse);
    const showFinanceApplicationError =
      financeApplicationError &&
      deal?.car_id === car?.id &&
      this.isFinanceOffersTab;

    // validation is currently disabled on this form due to issues with validating numbers on safari
    // https://app.clubhouse.io/getprodigy/story/19759/safari-field-validation-message-shown-though-given-valid-input-on-apr-of-purchase-tab
    if (this.state.viewForms) {
      return (
        <div className="pencil-editor-column">
          <div className="pencil-editor-headers">
            <HeaderNavigation
              viewForms={this.state.viewForms}
              goToPayments={() => {
                this.sendAmplitudeEvent(
                  amplitudeEvents.DESKING_PENCIL_FORMS_BACK_TO_DESKING_CLICK,
                );
                this.setState({ viewForms: false });
              }}
            />
          </div>
          <FormsAndAttachedFiles dealId={dealId} />
        </div>
      );
    }

    const crmDisable =
      !deal.crmVisitID ||
      !pencil?.id ||
      (!accepted && !!deal.lastOpenedInStore) ||
      isLoading ||
      !!pencil?.pushedToCrmAt;
    const disableCrmMenuItem = !deal.crmVisitID || !pencil?.id || isLoading;

    const selectedFinanceApplication = getFinanceApplicationForResponse(
      pencil,
      pencil?.selectedFinanceResponse,
    );
    const isCRMPushEnabled = this.isCRMPushEnabled() && car?.id === deal.car_id;
    const showPushToCRMButtonInHeader =
      isCRMPushEnabled &&
      this.isFinanceOffersTab &&
      deal?.selectedFinanceResponseId;
    const showPushToCRMForDesking = !!showPushToCRMButtonInHeader;
    const isDMSFlowEnabled = isDMSFlowEnabledUtil(dealer);
    const sentToDMS = isSentToDMS(deal);
    const showPushToDMSButtonInHeader =
      isDMSFlowEnabled &&
      this.isFinanceOffersTab &&
      deal?.selectedFinanceResponseId;
    const dmsDisable = sentToDMS;
    const lendingOffersProps = {
      updatePencil: this.updatePencil,
      updateFinanceApplicationError: msg => {
        this.setState({ financeApplicationError: msg });
      },
      setFinanceOfferCount: count => {
        this.setState({ financeOfferCount: count });
      },
      setFinanceApplicationCount: count => {
        this.setState({ financeApplicationCount: count });
      },
      selectedFinanceApplication,
      showRicTab: this.state.showRicTab,
      setShowRicTab: isShown => {
        this.setState({
          showRicTab: isShown,
        });
      },
      showCustomerSelectedOffer: this.state.showCustomerSelectedOffer,
      setShowCustomerSelectedOffer: isShown => {
        this.setState({
          showCustomerSelectedOffer: isShown,
        });
      },
      financeApplicationCount: this.state.financeApplicationCount,
      financeOfferCount: this.state.financeOfferCount,
    };

    const salesperson = (user || {}).firstName
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : '-';

    const formElement = (
      <form
        noValidate
        className="pencil-editor-container"
        ref={this.pencilEditorContainer}
        data-dd-privacy="allow"
      >
        <div className="pencil-editor-grid">
          <div className="row">
            <div className="col">
              <div className="row">
                {showFinanceApplicationError && (
                  <InlineMessage
                    message={financeApplicationError}
                    type="error"
                  />
                )}
                {!!this.state.showExpiredIncentiveNotice && (
                  <div className="pencil-warning-container">
                    <BiBell className="pencil-warning-icon" />
                    <div className="flex-1">
                      One or more expired incentives have been removed from
                      offers for this deal.
                    </div>
                    <MdClear
                      className="pencil-warning-icon pencil-warning-close"
                      onClick={() => {
                        this.setState({
                          showExpiredIncentiveNotice: false,
                        });
                      }}
                    />
                  </div>
                )}
                {!!this.state.showUnavailableAccessoriesNotice && (
                  <div className="pencil-warning-container">
                    <BiBell className="pencil-warning-icon" />
                    <div className="flex-1">
                      One or more unavailable accessories have been removed from
                      offers for this deal.
                    </div>
                    <MdClear
                      className="pencil-warning-icon pencil-warning-close"
                      onClick={() => {
                        this.setState({
                          showUnavailableAccessoriesNotice: false,
                        });
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="row">
                <div className="col pencil-editor-header">
                  <HeaderNavigation viewForms={this.state.viewForms} />
                  <div className="salesperson-name">
                    Salesperson: {salesperson}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col pencil-editor-header">
                  <div className="pencil-editor-nav">
                    {!this.props.pencilHistoryOpened && !isEmbeddedInFniHome() && (
                      <HeaderNotice
                        car={car}
                        deal={deal}
                        dealer={dealer}
                        user={user}
                        pencil={pencil}
                        onSendClick={() => {
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_SEND_CUSTOMER_CLICK,
                          );
                          // Validate pencil form before opening sticker
                          this.validatePencilForm();
                          this.setState({ showStickerModal: true });
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_SEND_SALESPERSON_START,
                          );
                        }}
                        onViewFormsClick={() => {
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_FORMS_CLICK,
                          );
                          this.setState({ viewForms: true });
                        }}
                        onAddNotesClick={() => {
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_NOTES_START,
                          );
                          this.setState({
                            showNotesModal: true,
                            notesReadAt: moment(),
                          });
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_NOTES_START,
                          );
                        }}
                        onOnlineHistoryClick={() => {
                          this.setState({ showOnlineHistoryModal: true });
                        }}
                        onTradeinsClick={() => {
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_TRADE_CLICK,
                          );
                          this.setState({ showTradeinsModal: true });
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PENCIL_TRADE_START,
                          );
                        }}
                        showViewForms
                        viewForms={this.state.viewForms}
                        hideUserDetails
                        showNotesNotificationIcon={this.areNewNotesAvailable()}
                      />
                    )}
                  </div>
                  <div className="pencil-editor-header-buttons">
                    <div className="push-to-crm">
                      {showPushToCRMForDesking && (
                        <button
                          type="button"
                          className="car-pencil-button pencil-header-button"
                          disabled={crmDisable}
                          onClick={this.pushToCRM}
                        >
                          Push to CRM
                        </button>
                      )}
                      {showPushToCRMForDesking &&
                        this.state.showPushToCRMTooltip &&
                        this.renderPushToCRMTooltip()}
                    </div>
                    {showPushToDMSButtonInHeader && (
                      <button
                        type="button"
                        className="car-pencil-button pencil-header-button"
                        onClick={() => {
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PUSHDMS_CLICK,
                          );
                          this.showDMSModal();
                          this.sendAmplitudeEvent(
                            amplitudeEvents.DESKING_PUSHDMS_START,
                          );
                        }}
                        disabled={dmsDisable}
                      >
                        Push to DMS
                      </button>
                    )}
                    <SendButton
                      dealId={dealId}
                      dealer={dealer}
                      currentUser={currentUser}
                      isFinanceOffersTab={this.isFinanceOffersTab}
                      origin={deal.origin}
                      disabled={
                        this.isSendDisabled() || this.props.pencilHistoryOpened
                      }
                      onSubmit={this.onSubmit}
                      lastOpenedInStore={deal?.lastOpenedInStore}
                      tooltip={this.getSendButtonTooltip()}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                {!this.props.pencilHistoryOpened && (
                  <PencilTopSection
                    car={car}
                    deal={deal}
                    selectedCars={selectedCars}
                    disableAddVehicle={this.isFinanceOffersTab}
                    onAdd={() => {
                      sendAddVehicleAmplitudeEvent(Action.CLICK, {
                        dealId: deal.id,
                      });
                      this.setState({
                        showVehicleSelection: !this.state.showVehicleSelection,
                      });
                      sendAddVehicleAmplitudeEvent(Action.START, {
                        dealId: deal.id,
                      });
                    }}
                    onSelect={c => {
                      if (c.id !== car.id) {
                        this.props.onCarSelect(c);
                      }
                    }}
                    onMakePrimary={c => {
                      actions.setPrimaryCarRequest(c);

                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_VEHICLE_PRIMARY_CLICK,
                      );
                    }}
                    onCustomerNameClick={() => {
                      this.setState({ showApplicantForm: true });
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_CUSTOMER_INFO_START,
                      );
                    }}
                    onRemove={c => {
                      const secondaryCarIds = (
                        deal.secondaryCarIds || []
                      ).filter(id => id !== c.id);

                      actions.dealUpdateRequest({
                        id: deal.id,
                        secondaryCarIds,
                      });
                      this.props.onRemoveCar();
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_VEHICLE_REMOVE_CLICK,
                      );
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="row">
            {!car && !this.state.showVehicleSelection && (
              <div className="col-md-12">
                <div className="pencil-editor-add-vehicle-container">
                  <p>No vehicles have been added to the deal yet.</p>
                  <button
                    className="send-pencil-button"
                    onClick={() =>
                      this.setState({
                        showVehicleSelection: true,
                      })
                    }
                  >
                    Add Vehicle
                  </button>
                </div>
              </div>
            )}
            <div className="col-md-12">
              {this.state.showVehicleSelection ? (
                <VehicleSelection
                  onClose={() => {
                    this.setState({
                      showVehicleSelection: !this.state.showVehicleSelection,
                    });
                  }}
                  onSelect={selectedCar => {
                    this.setState({
                      showVehicleSelection: !this.state.showVehicleSelection,
                    });

                    if (!deal.car_id) {
                      actions.dealUpdateRequest({
                        id: deal.id,
                        car_id: selectedCar.id,
                      });
                    } else if (
                      !selectedCars.some(c => c.id === selectedCar.id)
                    ) {
                      const secondaryCarIds = (deal.secondaryCarIds || [])
                        .concat(selectedCar.id)
                        .slice(-2);
                      actions.dealUpdateRequest({
                        id: deal.id,
                        secondaryCarIds,
                      });
                    }
                    this.props.onCarSelect(selectedCar);
                  }}
                  sendAddVehicleAmplitudeEvent={sendAddVehicleAmplitudeEvent}
                />
              ) : (
                <>
                  {validationErrors &&
                    validationErrors.map(validationError => (
                      <ErrorMessage error={validationError} />
                    ))}
                  {!!this.props.desking && (
                    <DeskingOffers
                      car={car}
                      deal={deal}
                      dealer={dealer}
                      dealerPriceRules={this.props.dealerPriceRules}
                      pencil={this.state.localPencil}
                      desking={this.props.desking}
                      deskingOffers={this.getDeskingOffers()}
                      errors={this.getDeskingOfferErrors()}
                      fetchingDesking={this.props.deskingFetching}
                      creatingOffer={this.props.creatingOffer}
                      deletingOffer={this.props.deletingOffer}
                      deskingPayments={this.getDeskingPayments()}
                      payments={payments}
                      onChange={this.updateDeskingOffer}
                      updateDeskingOffers={this.updateDeskingOffers}
                      onCreateDefaultOffer={this.createDefaultDeskingOffer}
                      onDuplicateOffer={this.duplicateDeskingOffer}
                      onRemoveOffer={this.removeDeskingOffer}
                      onPushToCRM={
                        this.isCRMPushEnabled()
                          ? this.pushDeskingOfferToCRM
                          : null
                      }
                      onPushToCRMDisabled={disableCrmMenuItem}
                      onPushToDMS={this.pushDeskingOfferToDMS}
                      onOpenPencilHistory={this.openPencilHistory}
                      on={this.updateOffer}
                      onResetToDefault={this.onResetToDefault}
                      changeLog={this.state.changeLog}
                      translations={translations}
                      toggleOnePayLease={this.toggleOnePayLease}
                      disabled={this.props.isLoading || !payments}
                      removeExpiredIncentivesRequest={
                        this.props.actions.removeExpiredIncentivesRequest
                      }
                      displayExpiredIncentiveNotice={() => {
                        this.setState({
                          showExpiredIncentiveNotice: true,
                        });
                        this.props.actions.pencilListRequest({
                          dealId,
                        });
                      }}
                      removeUnavailableAccessoriesRequest={
                        this.props.actions.removeUnavailableAccessoriesRequest
                      }
                      displayUnavailableAccessoriesNotice={() => {
                        this.setState({
                          showUnavailableAccessoriesNotice: true,
                        });
                        this.props.actions.pencilListRequest({
                          dealId,
                        });
                      }}
                      isFinanceOffersTab={this.isFinanceOffersTab}
                      setTabPath={this.setTabPath}
                      lendingOffersProps={lendingOffersProps}
                      currentUser={this.props.currentUser}
                      forwardDealToFNI={this.props.actions.forwardDealToFNI}
                      readOnlyMode={this.props.pencilHistoryOpened}
                      isReadOnlyLoading={
                        this.props.pencilHistoryOpened &&
                        this.props.fetchingVersionSnapshots
                      }
                      selectedIdForPencilHistory={this.getSelectedIdForPencilHistory()}
                      isViewingCurrentPencilVersion={
                        this.state.isViewingCurrentPencilVersion
                      }
                      financeResponses={this.state.deskingFinanceResponses}
                      setFinanceResponses={this.setDeskingFinanceResponses}
                    />
                  )}
                  <CrmNoteModal
                    isOpen={this.state.showOnlineHistoryModal}
                    crmNote={deal?.crmNote}
                    onClose={() =>
                      this.setState({ showOnlineHistoryModal: false })
                    }
                  />
                  <NotesModal
                    isOpen={this.state.showNotesModal}
                    onSubmit={() => {
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_NOTES_FINISH,
                      );
                      this.onSubmit();
                    }}
                    pencils={pencils}
                    note={this.state.note}
                    deal={this.props.deal}
                    onChange={this.onNoteChange}
                    dealer={dealer}
                    revert={this.revert}
                    carId={this.state.localPencil.carId}
                    pencilId={this.state.localPencil.id}
                    onClose={() => {
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_NOTES_CANCEL,
                      );
                      this.setState({
                        showNotesModal: false,
                        note: '', // Reset note when modal is closed
                      });
                    }}
                    onPushToDMS={this.showDMSModal}
                    onShowRicTab={e => {
                      e.preventDefault();
                      this.setTabPath(true);
                      this.setState({
                        showRicTab: true,
                        showCustomerSelectedOffer: true,
                      });
                    }}
                  />
                  <TradeInsModal
                    tradeInFields={[
                      {
                        isEditable: true,
                        key: 'id',
                      },
                      {
                        isEditable: true,
                        key: 'year',
                        required: true,
                      },
                      {
                        isEditable: true,
                        key: 'make',
                        required: true,
                      },
                      {
                        isEditable: true,
                        key: 'model',
                        required: true,
                      },
                      {
                        isEditable: true,
                        key: 'trim',
                      },
                      {
                        isEditable: true,
                        key: 'mileage',
                      },
                      {
                        isEditable: true,
                        key: 'tradeValue',
                        required: true,
                      },
                      {
                        isEditable: true,
                        key: 'payOff',
                      },
                    ]}
                    initialTradeIns={
                      this.props.desking?.deskingOffers[0]?.data?.pencilTradeIns?.filter(
                        t => t.id !== FAUX_TRADE_IN_ID,
                      ) ?? []
                    }
                    dealer={dealer}
                    dealerId={dealer.id}
                    updateTradeIns={trade => {
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_TRADE_FINISH,
                      );
                      this.updateDeskingOffersWithTradeIns(trade);
                    }}
                    isOpen={this.state.showTradeinsModal}
                    onClose={() => {
                      this.sendAmplitudeEvent(
                        amplitudeEvents.DESKING_PENCIL_TRADE_CANCEL,
                      );
                      this.setState({ showTradeinsModal: false });
                    }}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </form>
    );

    return (
      <div
        data-testid="pencil-editor"
        className={classNames('editor-history-container', {
          'pencil-editor-hidden': isEmbeddedStandaloneHistoryAndNotes(),
        })}
      >
        {formElement}
        <PencilHistory
          pencilHistoryOpened={this.props.pencilHistoryOpened}
          offerHistory={this.props.offerHistory}
          fetchingOfferHistory={this.props.fetchingOfferHistory}
          fetchingOfferHistoryError={this.props.fetchingOfferHistoryError}
          versionSnapshots={this.props.versionSnapshots}
          fetchingVersionSnapshots={this.props.fetchingVersionSnapshots}
          fetchingVersionSnapshotsError={
            this.props.fetchingVersionSnapshotsError
          }
          restoringVersion={this.props.restoringVersion}
          restoringVersionError={this.props.restoringVersionError}
          closePencilHistory={this.props.actions.closePencilHistory}
          renderVersionSnapshots={this.renderVersionSnapshots}
          restoreVersionRequest={this.props.actions.restoreVersionRequest}
          setIsViewingCurrentPencilVersion={
            this.setIsViewingCurrentPencilVersion
          }
        />
      </div>
    );
  };

  getSpinnerMessage = () => {
    if (this.props.isPushingToDMS) return 'Pushing deal to DMS...';
    if (this.props.pushingDeskingOfferToCRM) return 'Pushing offer to CRM...';
    return 'Loading... Just a moment...';
  };

  render() {
    const {
      deal,
      pencil,
      pencils,
      payments,
      isLoading,
      actions,
      pencilError,
      dealError,
      car,
      dealer,
      user,
      currentUser,
      desking,
      deskingFetching,
      sourceMapping,
      isPencilsMutating,
    } = this.props;

    const loadingMessage = this.getSpinnerMessage();
    const financeType = this.state.financeType;
    const loadingError = (pencilError || dealError || {}).error;
    let loadingErrorMsg;
    if (loadingError) {
      try {
        const parsedError = JSON.parse(loadingError);
        // ignore parsedError.error if it is empty object
        if (!isEmpty(parsedError.error)) {
          loadingErrorMsg = parsedError.error;
        } else {
          loadingErrorMsg = parsedError.message;
        }
      } catch (e) {
        const errorMessage =
          'There was an unexpected error loading the deal. Please try again and contact support if the problem persists.';
        // eslint-disable-next-line no-console
        console.error(errorMessage, e);
        loadingErrorMsg = errorMessage;
      }
    }

    const activeOffer = get(
      `data.${financeType}.${this.state.activeOffers[this.state.financeType] ||
        0}`,
    )(pencil);
    const pageLoading =
      !deal ||
      isLoading ||
      (!desking && deskingFetching) ||
      this.props.sendingDesking ||
      this.props.pushingDeskingOfferToCRM ||
      this.state.submittingForm;

    const { localPencil, note } = this.state;

    // avoid flicker by waiting for other props to fishinh loading into state
    const showEmbeddedNotes =
      !pageLoading && isEmbeddedStandaloneHistoryAndNotes();

    const getPageContent = () => (
      <>
        {pageLoading && !loadingErrorMsg ? (
          <Dimmer active inverted>
            <Loader inverted content={loadingMessage} />
          </Dimmer>
        ) : null}

        {loadingErrorMsg && (
          <div className="error-container">
            <span className="error-label">Error</span>
            {loadingErrorMsg}
          </div>
        )}

        {showEmbeddedNotes && (
          <EmbeddedNotes
            deal={deal}
            dealer={dealer}
            isPencilsMutating={isPencilsMutating}
            note={note}
            onShowRicTab={e => {
              e.preventDefault();
              this.setTabPath(true);
              this.setState({
                showRicTab: true,
                showCustomerSelectedOffer: true,
              });
            }}
            onPushToDMS={this.showDMSModal}
            onChange={this.onNoteChange}
            onSubmit={() => {
              this.sendAmplitudeEvent(
                amplitudeEvents.DESKING_PENCIL_NOTES_FINISH,
              );
              this.onSubmit();
            }}
            pencilId={localPencil.id}
            pencils={pencils}
          />
        )}

        <SentModal
          data-dd-privacy="allow"
          text={this.props.pencilModalText}
          ref={ref => {
            this.modal = ref;
          }}
        />

        {this.state.showApplicantForm && (
          <CustomerFormPanel
            deal={deal}
            dealer={dealer}
            onClose={() => this.setState({ showApplicantForm: false })}
            sourceMapping={sourceMapping}
            updateDeal={async updatedDeal => {
              await actions.dealCRMPushRequest({
                ...updatedDeal,
                customerInfoSubmittedAt: new Date().toISOString(),
              });
            }}
          />
        )}
        {/* this.state.showStickerModal check is being used to prevent errors
          while all required resources are loading */}
        {this.state.showStickerModal && car && (
          <StickerModal
            deal={deal}
            car={car}
            dealer={dealer}
            userForMessage={user || currentUser}
            financeType={financeType}
            pencil={createPencilFromDesking(
              this.state.localPencil,
              this.props.desking,
              dealer,
            )}
            isOpen
            onPrint={this.onPrintSticker}
            onSend={this.onSendSticker}
            onClose={() => this.setState({ showStickerModal: false })}
            offerCategory={
              getPaymentOffer(payments, financeType, activeOffer)?.category
                ?.type
            }
            deskingId={this.props.desking?.id}
          />
        )}

        {this.state.showDMSCustomerModal && (
          <DMSCustomerModal
            deal={deal}
            dealer={dealer}
            isOpen={this.state.showDMSCustomerModal}
            onClose={() => this.setState({ showDMSCustomerModal: false })}
            car={car}
          />
        )}

        {this.state.showDMSCustomerModalForOffer && (
          <DMSCustomerModal
            deskingOffer={this.state.deskingOfferForDMSPush}
            deal={deal}
            dealer={dealer}
            isOpen={this.state.showDMSCustomerModalForOffer}
            onClose={() =>
              this.setState({ showDMSCustomerModalForOffer: false })
            }
            car={car}
            updateDeskingTradeIns={this.updateDeskingOffersWithTradeIns}
          />
        )}

        {deal ? this.renderContent() : null}
      </>
    );
    return isEmbeddedInCommandCenter() ? (
      <div style={{ height: '100%' }}>{getPageContent()}</div>
    ) : (
      <SidebarContainer title="Desking">{getPageContent()}</SidebarContainer>
    );
  }
}

CarPencilEditor.propTypes = {
  actions: PropTypes.object,
  match: PropTypes.object,
  pencil: PropTypes.object,
  pencils: PropTypes.array,
  payments: PropTypes.object,
  dealer: PropTypes.object,
  deal: PropTypes.object,
  car: PropTypes.object,
  user: PropTypes.object,
  currentUser: PropTypes.object,
  isLoading: PropTypes.bool,
  isPencilsMutating: PropTypes.bool,
  isPushingToDMS: PropTypes.bool,
  pencilModalText: PropTypes.string,
  selectedCars: PropTypes.array,
  onCarSelect: PropTypes.func,
  allPencils: PropTypes.array,
  pencilError: PropTypes.object,
  desking: PropTypes.object,
  deskingError: PropTypes.object,
  deskingFetching: PropTypes.bool,
  deskingPayments: PropTypes.array,
  deskingPaymentsError: PropTypes.object,
  dealError: PropTypes.object,
  location: PropTypes.object,
  translations: PropTypes.object,
  dealerPriceRules: PropTypes.object,
  onRemoveCar: PropTypes.func,
  sendingDesking: PropTypes.bool,
  creatingOffer: PropTypes.bool,
  deletingOffer: PropTypes.bool,
  pushingDeskingOfferToCRM: PropTypes.bool,
  offerPymtCalcInProgMap: PropTypes.object,
};

const mapStateToProps = ({
  deals,
  dealers,
  pencils,
  desking,
  deskingPayments,
  user,
  forms,
  translations,
  pen,
  global,
  pencilHistory,
  sourceMappings,
}) => ({
  isLoading:
    deals.fetching ||
    deals.sendingSticker ||
    deals.pushingToCRM ||
    deals.pushingToDMS,
  isPencilsMutating: deals.isPencilsMutating,
  isPushingToDMS: deals.pushingToDMS,
  pencilModalText: deals.pencilModalText,
  payments: deals.payments,
  dealer: deals.dealer,
  deal: deals.deal,
  user: deals.user,
  currentUser: user.currentUser,
  selectedCars: deals.selectedCars,
  allPencils: pencils.data,
  pencilError: pencils.error,
  desking: desking.desking,
  deskingError: desking.error,
  deskingFetching: desking.fetching,
  offerPymtCalcInProgMap: deskingPayments.paymentCalcInProgress,
  deskingPayments: deskingPayments.payments,
  deskingPaymentsError: deskingPayments.error,
  dealError: deals.error,
  translations: translations.data,
  dealerPriceRules: dealers.dealerPriceRules,
  sendingDesking: desking.sendingDesking,
  creatingOffer: desking.creatingOffer,
  deletingOffer: desking.deletingOffer,
  pushingDeskingOfferToCRM: desking.pushingToCRM,
  forms,
  sidebarOpened: global.sidebarOpened,
  pencilHistoryOpened: pencilHistory.pencilHistoryOpened,
  offerHistory: pencilHistory.offerHistory,
  fetchingOfferHistory: pencilHistory.fetchingOfferHistory,
  fetchingOfferHistoryError: pencilHistory.fetchingOfferHistoryError,
  versionSnapshots: pencilHistory.versionSnapshots,
  fetchingVersionSnapshots: pencilHistory.fetchingVersionSnapshots,
  fetchingVersionSnapshotsError: pencilHistory.fetchingVersionSnapshotsError,
  restoringVersion: pencilHistory.restoringVersion,
  restoringVersionError: pencilHistory.restoringVersionError,
  sourceMapping: sourceMappings.sourceMapping,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...dealsActions,
      ...pencilsActions,
      ...deskingActions,
      ...formsActions,
      ...translationsActions,
      ...dealerActions,
      ...pencilHistoryActions,
      ...sourceMappingActions,
    },
    dispatch,
  ),
});

export const test = {
  updateWebsocketSubscriptionsImpl,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CarPencilEditor),
);
